import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  BellRing,
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  ChevronRight,
  Circle,
  CreditCard,
  Inbox,
  Landmark,
  Loader2,
  Mail,
  Megaphone,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { linkToBooking, linkToLead, linkToPayout } from '../../utils/entityLinks';
import {
  deleteAllNotifications,
  deleteNotification,
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../../services/api';
import {
  dismissStaffAlert,
  getStaffAlertUnreadCount,
  listStaffAlerts,
  markAllStaffAlertsRead,
  markStaffAlertRead,
} from '../../services/staffAlertsApi';
import {
  formatStaffAlertEventType,
  getSafeStaffAlertUrl,
  getStaffAlertContextChips,
  getStaffAlertCtaLabel,
  isStaffAlertEndpointUnavailable,
} from '../../utils/staffAlerts.mjs';

const POLL_INTERVAL_MS = 30000;
const TAB_STAFF = 'staff';
const TAB_OPERATIONS = 'operations';

const OPERATION_TYPE_LABELS = {
  guest_payment_due: 'Payment Due',
  owner_payout_pending: 'Owner Payout',
  new_booking_created: 'Booking Created',
  security_refund_due: 'Security Deposit',
  new_lead: 'New Lead',
  approval_pending: 'Needs Approval',
  rm_contact_change: 'Contact Change (FYI)',
  financial_drift: 'Financial Drift',
};

const OPERATION_TYPE_ICONS = {
  guest_payment_due: CreditCard,
  owner_payout_pending: Landmark,
  new_booking_created: BellRing,
  security_refund_due: ShieldCheck,
  new_lead: Mail,
  approval_pending: CheckCircle2,
  rm_contact_change: Megaphone,
  financial_drift: Landmark,
};

const UNAVAILABLE_NETWORK_CODES = new Set([
  'ECONNREFUSED',
  'ERR_NETWORK',
  'ERR_CONNECTION_REFUSED',
  'ECONNABORTED',
]);

function hasStoredToken() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.localStorage.getItem('token') || window.localStorage.getItem('access_token'));
}

function normalizeAlerts(data) {
  return Array.isArray(data?.alerts) ? data.alerts : [];
}

function normalizeOperationalNotifications(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.notifications)) return data.notifications;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function titleCase(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatAlertDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatOperationalDueDate(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Overdue';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return `In ${diffDays} days`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatOperationalNotificationType(type) {
  return OPERATION_TYPE_LABELS[type] || titleCase(type) || 'Reminder';
}

function getOperationalNotificationIcon(type) {
  return OPERATION_TYPE_ICONS[type] || Megaphone;
}

function getOperationalNotificationUrl(notification = {}) {
  if (notification.change_request_id) {
    return `/approvals/contact/${encodeURIComponent(notification.change_request_id)}`;
  }
  if (notification.lead_id) {
    return linkToLead(notification.lead_id);
  }
  // owner_payout_pending carries both owner_payout_id and exclusive_booking_id — route
  // it to the specific payout (checked before the booking so the payout wins). Only
  // this notification type carries owner_payout_id, so other types are unaffected.
  if (notification.owner_payout_id) {
    return linkToPayout(notification.owner_payout_id);
  }
  if (notification.exclusive_booking_id) {
    return linkToBooking({ exclusive_booking_id: notification.exclusive_booking_id });
  }
  if (notification.villa_id) {
    return `/villas/${encodeURIComponent(notification.villa_id)}`;
  }
  return null;
}

function isOperationalNotificationUnread(notification = {}) {
  return !notification.is_read && !notification.read_at;
}

function isOperationalEndpointUnavailable(error) {
  const status = error?.response?.status;
  if (status === 404 || status >= 500) return true;
  if (UNAVAILABLE_NETWORK_CODES.has(error?.code)) return true;

  const message = String(error?.message || '').toLowerCase();
  return message.includes('network error') || message.includes('connection refused');
}

export default function StaffNotificationCenter() {
  const navigate = useNavigate();
  const { user, accessToken, loading: authLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_STAFF);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [endpointAvailable, setEndpointAvailable] = useState(true);
  const [operationalNotifications, setOperationalNotifications] = useState([]);
  const [operationalUnreadCount, setOperationalUnreadCount] = useState(0);
  const [operationalLoading, setOperationalLoading] = useState(false);
  const [operationalEndpointAvailable, setOperationalEndpointAvailable] = useState(true);
  const [activeSwipe, setActiveSwipe] = useState({ id: null, offsetX: 0 });
  const touchStateRef = useRef(null);
  const suppressNextClickRef = useRef(null);
  const tabSelectedByUserRef = useRef(false);

  const isAuthenticated = useMemo(() => {
    if (authLoading) return false;
    return Boolean(user || accessToken || hasStoredToken());
  }, [accessToken, authLoading, user]);

  const totalUnreadCount = useMemo(
    () => Math.max(0, Number(unreadCount) || 0) + Math.max(0, Number(operationalUnreadCount) || 0),
    [operationalUnreadCount, unreadCount],
  );

  const activeUnreadCount = activeTab === TAB_OPERATIONS ? operationalUnreadCount : unreadCount;

  const getPreferredTab = useCallback(() => {
    if (unreadCount > 0 || alerts.length > 0) return TAB_STAFF;
    if (operationalUnreadCount > 0 || operationalNotifications.length > 0) return TAB_OPERATIONS;
    return TAB_STAFF;
  }, [alerts.length, operationalNotifications.length, operationalUnreadCount, unreadCount]);

  const loadUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const { data } = await getStaffAlertUnreadCount();
      setUnreadCount(Number(data?.count) || 0);
      setEndpointAvailable(true);
    } catch (err) {
      if (isStaffAlertEndpointUnavailable(err)) {
        setEndpointAvailable(false);
      }
      setUnreadCount(0);
    }
  }, [isAuthenticated]);

  const loadOperationalUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const { data } = await getUnreadNotificationCount();
      setOperationalUnreadCount(Number(data?.count) || 0);
      setOperationalEndpointAvailable(true);
    } catch (err) {
      if (isOperationalEndpointUnavailable(err)) {
        setOperationalEndpointAvailable(false);
      }
      setOperationalUnreadCount(0);
    }
  }, [isAuthenticated]);

  const loadAlerts = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const { data } = await listStaffAlerts({ limit: 20 });
      setAlerts(normalizeAlerts(data));
      setEndpointAvailable(true);
    } catch (err) {
      if (isStaffAlertEndpointUnavailable(err)) {
        setEndpointAvailable(false);
      }
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadOperationalNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    setOperationalLoading(true);
    try {
      const { data } = await getNotifications();
      setOperationalNotifications(normalizeOperationalNotifications(data));
      setOperationalEndpointAvailable(true);
    } catch (err) {
      if (isOperationalEndpointUnavailable(err)) {
        setOperationalEndpointAvailable(false);
      }
      setOperationalNotifications([]);
    } finally {
      setOperationalLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setOperationalUnreadCount(0);
      setAlerts([]);
      setOperationalNotifications([]);
      setIsOpen(false);
      setActiveTab(TAB_STAFF);
      setActiveSwipe({ id: null, offsetX: 0 });
      touchStateRef.current = null;
      tabSelectedByUserRef.current = false;
      return undefined;
    }

    loadUnreadCount();
    loadOperationalUnreadCount();
    const intervalId = window.setInterval(() => {
      loadUnreadCount();
      loadOperationalUnreadCount();
    }, POLL_INTERVAL_MS);
    const handleFocus = () => {
      loadUnreadCount();
      loadOperationalUnreadCount();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, loadOperationalUnreadCount, loadUnreadCount]);

  useEffect(() => {
    if (isOpen) {
      loadAlerts();
      loadOperationalNotifications();
    }
  }, [isOpen, loadAlerts, loadOperationalNotifications]);

  useEffect(() => {
    if (!isOpen || tabSelectedByUserRef.current) return;
    setActiveTab(getPreferredTab());
  }, [getPreferredTab, isOpen]);

  const resetSwipe = useCallback(() => {
    touchStateRef.current = null;
    setActiveSwipe({ id: null, offsetX: 0 });
  }, []);

  const handleOpenChange = () => {
    resetSwipe();
    setIsOpen((current) => {
      const nextOpen = !current;
      if (nextOpen) {
        tabSelectedByUserRef.current = false;
        setActiveTab(getPreferredTab());
      }
      return nextOpen;
    });
  };

  const handleTabChange = (tab) => {
    tabSelectedByUserRef.current = true;
    resetSwipe();
    setActiveTab(tab);
  };

  const handleDismissAlert = useCallback(async (alert, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    resetSwipe();

    const wasUnread = !alert.read_at;
    setAlerts((current) => current.filter((item) => item.id !== alert.id));
    if (wasUnread) {
      setUnreadCount((current) => Math.max(0, current - 1));
    }

    try {
      await dismissStaffAlert(alert.id);
    } catch (_) {
      setAlerts((current) => (
        current.some((item) => item.id === alert.id) ? current : [alert, ...current]
      ));
      if (wasUnread) {
        setUnreadCount((current) => current + 1);
      }
    }
  }, [resetSwipe]);

  const handleAlertTouchStart = useCallback((alert, event) => {
    const touch = event.touches?.[0];
    if (!touch) return;

    touchStateRef.current = {
      id: alert.id,
      startX: touch.clientX,
      startY: touch.clientY,
      deltaX: 0,
      deltaY: 0,
      isHorizontal: false,
      isVertical: false,
    };
    setActiveSwipe({ id: null, offsetX: 0 });
  }, []);

  const handleAlertTouchMove = useCallback((alert, event) => {
    const touch = event.touches?.[0];
    const state = touchStateRef.current;
    if (!touch || !state || state.id !== alert.id) return;

    const deltaX = touch.clientX - state.startX;
    const deltaY = touch.clientY - state.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    state.deltaX = deltaX;
    state.deltaY = deltaY;

    if (!state.isHorizontal && !state.isVertical) {
      if (absY > 12 && absY > absX) {
        state.isVertical = true;
      } else if (absX > 12 && absX > absY * 1.25) {
        state.isHorizontal = true;
      }
    }

    if (!state.isHorizontal) return;
    if (event.cancelable) event.preventDefault();

    setActiveSwipe({
      id: alert.id,
      offsetX: Math.max(-96, Math.min(0, deltaX)),
    });
  }, []);

  const handleAlertTouchEnd = useCallback((alert, event) => {
    const state = touchStateRef.current;
    if (!state || state.id !== alert.id) return;

    const shouldDismiss = state.isHorizontal
      && state.deltaX <= -70
      && Math.abs(state.deltaX) > Math.abs(state.deltaY) * 1.25;

    if (shouldDismiss) {
      suppressNextClickRef.current = alert.id;
      handleDismissAlert(alert, event);
      return;
    }

    resetSwipe();
  }, [handleDismissAlert, resetSwipe]);

  const handleAlertClick = async (alert) => {
    if (suppressNextClickRef.current === alert.id) {
      suppressNextClickRef.current = null;
      return;
    }

    const safeUrl = getSafeStaffAlertUrl(alert.url);
    const wasUnread = !alert.read_at;

    if (wasUnread) {
      setAlerts((current) => current.map((item) => (
        item.id === alert.id ? { ...item, read_at: new Date().toISOString() } : item
      )));
      setUnreadCount((current) => Math.max(0, current - 1));
    }

    try {
      await markStaffAlertRead(alert.id);
    } catch (_) {
      // Missing backend should never interrupt navigation from the center.
    }

    setIsOpen(false);
    if (safeUrl) {
      navigate(safeUrl);
    }
  };

  const handleOperationalNotificationRead = useCallback(async (notification) => {
    if (!notification?.id || !isOperationalNotificationUnread(notification)) return;

    setOperationalNotifications((current) => current.map((item) => (
      item.id === notification.id ? { ...item, is_read: true, read_at: item.read_at || new Date().toISOString() } : item
    )));
    setOperationalUnreadCount((current) => Math.max(0, current - 1));

    try {
      await markNotificationAsRead(notification.id);
    } catch (_) {
      // Operational reminders stay optimistic to avoid noisy legacy endpoint errors.
    }
  }, []);

  const handleOperationalNotificationClick = (notification) => {
    if (isOperationalNotificationUnread(notification)) {
      handleOperationalNotificationRead(notification);
    }

    const url = getOperationalNotificationUrl(notification);
    setIsOpen(false);
    if (url) {
      navigate(url);
    }
  };

  const handleOperationalNotificationKeyDown = (notification, event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleOperationalNotificationClick(notification);
  };

  const handleMarkAllStaffRead = async () => {
    setUnreadCount(0);
    setAlerts((current) => current.map((alert) => ({
      ...alert,
      read_at: alert.read_at || new Date().toISOString(),
    })));

    try {
      await markAllStaffAlertsRead();
    } catch (_) {
      // Optimistic local update is enough during frontend migration.
    }
  };

  const handleMarkAllOperationalRead = async () => {
    setOperationalUnreadCount(0);
    setOperationalNotifications((current) => current.map((notification) => ({
      ...notification,
      is_read: true,
      read_at: notification.read_at || new Date().toISOString(),
    })));

    try {
      await markAllNotificationsAsRead();
    } catch (_) {
      // Legacy endpoint failures should not interrupt the consolidated panel.
    }
  };

  const handleDeleteOperationalNotification = useCallback(async (notification) => {
    if (!notification?.id) return;

    const wasUnread = isOperationalNotificationUnread(notification);
    setOperationalNotifications((current) => current.filter((item) => item.id !== notification.id));
    if (wasUnread) {
      setOperationalUnreadCount((current) => Math.max(0, current - 1));
    }

    try {
      await deleteNotification(notification.id);
    } catch (_) {
      setOperationalNotifications((current) => (
        current.some((item) => item.id === notification.id) ? current : [notification, ...current]
      ));
      if (wasUnread) {
        setOperationalUnreadCount((current) => current + 1);
      }
    }
  }, []);

  const handleDeleteAllOperational = useCallback(async () => {
    const previousNotifications = operationalNotifications;
    const previousUnreadCount = operationalUnreadCount;

    setOperationalNotifications([]);
    setOperationalUnreadCount(0);

    try {
      await deleteAllNotifications();
    } catch (_) {
      setOperationalNotifications(previousNotifications);
      setOperationalUnreadCount(previousUnreadCount);
    }
  }, [operationalNotifications, operationalUnreadCount]);

  const handleMarkAllRead = () => {
    if (activeTab === TAB_OPERATIONS) {
      handleMarkAllOperationalRead();
      return;
    }

    handleMarkAllStaffRead();
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleOpenChange}
        className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 hover:text-[#1e2a2e] active:scale-[0.98] dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
        aria-label="Villa Experience alerts"
        aria-expanded={isOpen}
      >
        <Bell size={20} />
        {totalUnreadCount > 0 && (
          <span className="absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#cfa190] px-1 text-[11px] font-bold leading-none text-[#1e2a2e] ring-2 ring-white dark:ring-slate-900">
            {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[58] cursor-default bg-slate-950/10"
            onClick={() => setIsOpen(false)}
            aria-label="Close Villa Experience alerts"
          />
          <div className="fixed left-2 right-2 top-16 z-[60] flex max-h-[calc(100dvh-5rem)] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_20px_50px_rgba(30,42,46,0.18)] sm:left-auto sm:right-4 sm:w-[430px] dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-[#1e2a2e] px-4 py-4 text-white dark:border-slate-700">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#f2d7cc]">
                  Villa Experience
                </div>
                <h3 className="mt-1 text-base font-semibold">Villa Experience Alerts</h3>
              </div>
              <div className="flex items-center gap-1">
                {activeUnreadCount > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllRead}
                    className="inline-flex min-h-10 items-center gap-1 rounded-md px-2.5 text-xs font-semibold text-[#f2d7cc] transition hover:bg-white/10 hover:text-white"
                  >
                    <CheckCheck size={15} />
                    <span>Mark all read</span>
                  </button>
                )}
                {activeTab === TAB_OPERATIONS && operationalNotifications.length > 0 && (
                  <button
                    type="button"
                    onClick={handleDeleteAllOperational}
                    className="inline-flex min-h-10 items-center gap-1 rounded-md px-2.5 text-xs font-semibold text-white/70 transition hover:bg-white/10 hover:text-red-100"
                  >
                    <Trash2 size={15} />
                    <span>Clear all</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-md text-white/75 transition hover:bg-white/10 hover:text-white"
                  aria-label="Close Villa Experience alerts"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="border-b border-[#ead6cf] bg-[#fffaf6] p-2 dark:border-slate-800 dark:bg-slate-900">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => handleTabChange(TAB_STAFF)}
                  className={`min-h-[68px] rounded-md border px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#cfa190] ${
                    activeTab === TAB_STAFF
                      ? 'border-[#cfa190] bg-white shadow-sm dark:bg-slate-800'
                      : 'border-transparent bg-[#fff7f2] hover:border-[#ead6cf] hover:bg-white dark:bg-slate-900 dark:hover:bg-slate-800'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-[#1e2a2e] dark:text-white">Staff</span>
                    {unreadCount > 0 && (
                      <span className="rounded-full bg-[#cfa190] px-2 py-0.5 text-xs font-bold text-[#1e2a2e]">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Live alerts for leads, chats, assignments.
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange(TAB_OPERATIONS)}
                  className={`min-h-[68px] rounded-md border px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#cfa190] ${
                    activeTab === TAB_OPERATIONS
                      ? 'border-[#cfa190] bg-white shadow-sm dark:bg-slate-800'
                      : 'border-transparent bg-[#fff7f2] hover:border-[#ead6cf] hover:bg-white dark:bg-slate-900 dark:hover:bg-slate-800'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-[#1e2a2e] dark:text-white">Operations</span>
                    {operationalUnreadCount > 0 && (
                      <span className="rounded-full bg-[#cfa190] px-2 py-0.5 text-xs font-bold text-[#1e2a2e]">
                        {operationalUnreadCount > 99 ? '99+' : operationalUnreadCount}
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Due-date and booking reminders.
                  </span>
                </button>
              </div>
            </div>

            <div className="min-h-[180px] flex-1 overflow-y-auto">
              {activeTab === TAB_STAFF && (
                <>
                  {loading ? (
                    <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 px-4 py-8 text-sm text-slate-500 dark:text-slate-400">
                      <Loader2 size={22} className="animate-spin text-[#cfa190]" />
                      <span>Loading staff alerts</span>
                    </div>
                  ) : !endpointAvailable ? (
                    <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 px-6 py-8 text-center">
                      <Inbox size={28} className="text-slate-300 dark:text-slate-600" />
                      <div>
                        <p className="text-sm font-semibold text-[#1e2a2e] dark:text-white">
                          Staff Alert Center is not active here yet.
                        </p>
                        <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                          Alerts will appear after the backend migration and event hooks are deployed.
                        </p>
                      </div>
                    </div>
                  ) : alerts.length === 0 ? (
                    <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 px-6 py-8 text-center">
                      <Inbox size={28} className="text-slate-300 dark:text-slate-600" />
                      <div>
                        <p className="text-sm font-semibold text-[#1e2a2e] dark:text-white">No staff alerts</p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          Live alerts for leads, chats, and assignments will collect here.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                      {alerts.map((alert) => {
                        const unread = !alert.read_at;
                        const chips = getStaffAlertContextChips(alert).slice(0, 3);
                        const swipeOffset = activeSwipe.id === alert.id ? activeSwipe.offsetX : 0;

                        return (
                          <div
                            key={alert.id}
                            onTouchStart={(event) => handleAlertTouchStart(alert, event)}
                            onTouchMove={(event) => handleAlertTouchMove(alert, event)}
                            onTouchEnd={(event) => handleAlertTouchEnd(alert, event)}
                            onTouchCancel={resetSwipe}
                            className={`group/alert flex w-full items-stretch transition-[background-color,transform] duration-150 ease-out hover:bg-[#fff7f2] active:bg-[#fff0e8] dark:hover:bg-slate-800 ${
                              unread ? 'bg-[#fffaf6] dark:bg-slate-900' : 'bg-white dark:bg-slate-900'
                            }`}
                            style={{
                              touchAction: 'pan-y',
                              transform: swipeOffset ? `translateX(${swipeOffset}px)` : undefined,
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => handleAlertClick(alert)}
                              className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] gap-3 py-4 pl-4 pr-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#cfa190]"
                            >
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-xs font-semibold uppercase tracking-wide text-[#b88573]">
                                    {formatStaffAlertEventType(alert.event_type)}
                                  </span>
                                  {unread && (
                                    <span className="h-2 w-2 rounded-full bg-[#cfa190]" aria-label="Unread" />
                                  )}
                                </div>
                                <div className={`mt-1 text-sm leading-5 ${unread ? 'font-semibold text-[#1e2a2e] dark:text-white' : 'font-medium text-slate-700 dark:text-slate-200'}`}>
                                  {alert.title || 'Staff alert'}
                                </div>
                                {alert.body && (
                                  <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-500 dark:text-slate-400">
                                    {alert.body}
                                  </p>
                                )}
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  {chips.map((chip) => (
                                    <span
                                      key={chip}
                                      className="rounded-md border border-[#ead6cf] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#42535a] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                    >
                                      {chip}
                                    </span>
                                  ))}
                                  {alert.created_at && (
                                    <span className="text-xs text-slate-400">
                                      {formatAlertDate(alert.created_at)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex min-w-[74px] flex-col items-end justify-between gap-3">
                                <ChevronRight size={18} className="mt-1 text-slate-300 transition group-hover/alert:text-[#cfa190]" />
                                {getSafeStaffAlertUrl(alert.url) && (
                                  <span className="text-xs font-semibold text-[#b88573]">
                                    {getStaffAlertCtaLabel(alert)}
                                  </span>
                                )}
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={(event) => handleDismissAlert(alert, event)}
                              className="mx-3 my-4 flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-transparent text-slate-400 transition hover:border-[#ead6cf] hover:bg-white hover:text-[#8f675a] active:scale-[0.96] dark:text-slate-500 dark:hover:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-[#f2d7cc]"
                              aria-label="Dismiss alert"
                              title="Dismiss alert"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {activeTab === TAB_OPERATIONS && (
                <>
                  {operationalLoading ? (
                    <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 px-4 py-8 text-sm text-slate-500 dark:text-slate-400">
                      <Loader2 size={22} className="animate-spin text-[#cfa190]" />
                      <span>Loading operational reminders</span>
                    </div>
                  ) : !operationalEndpointAvailable ? (
                    <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 px-6 py-8 text-center">
                      <Inbox size={28} className="text-slate-300 dark:text-slate-600" />
                      <div>
                        <p className="text-sm font-semibold text-[#1e2a2e] dark:text-white">
                          Operational reminders are unavailable right now.
                        </p>
                        <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                          Due-date and booking reminders will appear when the notifications endpoint is available.
                        </p>
                      </div>
                    </div>
                  ) : operationalNotifications.length === 0 ? (
                    <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 px-6 py-8 text-center">
                      <Inbox size={28} className="text-slate-300 dark:text-slate-600" />
                      <div>
                        <p className="text-sm font-semibold text-[#1e2a2e] dark:text-white">No operational reminders</p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          Payments, bookings, and security deposit reminders will collect here.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                      {operationalNotifications.map((notification) => {
                        const unread = isOperationalNotificationUnread(notification);
                        const Icon = getOperationalNotificationIcon(notification.type);
                        const dueLabel = formatOperationalDueDate(notification.due_date);
                        const createdLabel = formatAlertDate(notification.created_at);
                        const destinationUrl = getOperationalNotificationUrl(notification);

                        return (
                          <div
                            key={notification.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleOperationalNotificationClick(notification)}
                            onKeyDown={(event) => handleOperationalNotificationKeyDown(notification, event)}
                            className={`group/operation grid min-h-[72px] cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] gap-3 px-4 py-4 transition hover:bg-[#fff7f2] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#cfa190] active:bg-[#fff0e8] dark:hover:bg-slate-800 ${
                              unread ? 'bg-[#fffaf6] dark:bg-slate-900' : 'bg-white dark:bg-slate-900'
                            }`}
                          >
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#ead6cf] bg-[#fff7f2] text-[#8f675a] dark:border-slate-700 dark:bg-slate-800 dark:text-[#f2d7cc]">
                              <Icon size={18} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-[#b88573]">
                                  {formatOperationalNotificationType(notification.type)}
                                </span>
                                {unread && (
                                  <span className="h-2 w-2 rounded-full bg-[#cfa190]" aria-label="Unread" />
                                )}
                              </div>
                              <div className={`mt-1 text-sm leading-5 ${unread ? 'font-semibold text-[#1e2a2e] dark:text-white' : 'font-medium text-slate-700 dark:text-slate-200'}`}>
                                {notification.title || 'Operational reminder'}
                              </div>
                              {notification.message && (
                                <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-500 dark:text-slate-400">
                                  {notification.message}
                                </p>
                              )}
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                {dueLabel && (
                                  <span className="inline-flex items-center gap-1 rounded-md border border-[#ead6cf] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#42535a] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                    <CalendarClock size={12} />
                                    Due: {dueLabel}
                                  </span>
                                )}
                                {createdLabel && (
                                  <span className="text-xs text-slate-400">
                                    {createdLabel}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex min-w-[82px] flex-col items-end justify-between gap-3">
                              <div className="flex items-center gap-1">
                                {unread ? (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOperationalNotificationRead(notification);
                                    }}
                                    className="flex h-10 w-10 items-center justify-center rounded-md text-slate-400 transition hover:bg-white hover:text-[#8f675a] dark:hover:bg-slate-800 dark:hover:text-[#f2d7cc]"
                                    aria-label="Mark operational reminder as read"
                                    title="Mark as read"
                                  >
                                    <Circle size={16} />
                                  </button>
                                ) : (
                                  <span className="flex h-10 w-10 items-center justify-center text-emerald-500">
                                    <CheckCircle2 size={16} />
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteOperationalNotification(notification);
                                  }}
                                  className="flex h-10 w-10 items-center justify-center rounded-md text-slate-400 transition hover:bg-white hover:text-red-500 dark:hover:bg-slate-800 dark:hover:text-red-300"
                                  aria-label="Delete operational reminder"
                                  title="Delete notification"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                              {destinationUrl && (
                                <ChevronRight size={18} className="text-slate-300 transition group-hover/operation:text-[#cfa190]" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {activeTab === TAB_STAFF && endpointAvailable && !loading && alerts.length > 0 && (
              <div className="border-t border-slate-100 px-4 py-2 text-xs font-medium text-slate-500 dark:border-slate-800 dark:text-slate-400">
                Swipe left or tap X to dismiss.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
